import bindAll from 'lodash.bindall'
import PropTypes from 'prop-types'
import React from 'react'
import VM from 'scratch-vm'

import { connect } from 'react-redux'

import { updateTargets } from '../reducers/targets'
import { updateBlockDrag } from '../reducers/block-drag'
import { updateMonitors } from '../reducers/monitors'
import {
    setProjectChanged,
    setProjectUnchanged,
} from '../reducers/project-changed'
import {
    setRunningState,
    setTurboState,
    setStartedState,
} from '../reducers/vm-status'
import { showExtensionAlert } from '../reducers/alerts'
import { updateMicIndicator } from '../reducers/mic-indicator'
import { parseEachBlock } from '../reducers/code-generator'
import { setCodes } from '../reducers/script'

let timeOut = false

/*
 * Higher Order Component to manage events emitted by the VM
 * @param {React.Component} WrappedComponent component to manage VM events for
 * @returns {React.Component} connected component with vm events bound to redux
 */
const vmListenerHOC = function (WrappedComponent) {
    class VMListener extends React.Component {
        constructor(props) {
            super(props)

            this.codeGenTimer = null
            this.codeGenPending = false

            bindAll(this, [
                'handleKeyDown',
                'handleKeyUp',
                'handleProjectChanged',
                'handleTargetsUpdate',
                'debouncedCodeGenerator',
            ])
            // We have to start listening to the vm here rather than in
            // componentDidMount because the HOC mounts the wrapped component,
            // so the HOC componentDidMount triggers after the wrapped component
            // mounts.
            // If the wrapped component uses the vm in componentDidMount, then
            // we need to start listening before mounting the wrapped component.
            this.props.vm.on('targetsUpdate', this.handleTargetsUpdate)
            this.props.vm.on('MONITORS_UPDATE', this.props.onMonitorsUpdate)
            this.props.vm.on('BLOCK_DRAG_UPDATE', this.props.onBlockDragUpdate)
            this.props.vm.on('TURBO_MODE_ON', this.props.onTurboModeOn)
            this.props.vm.on('TURBO_MODE_OFF', this.props.onTurboModeOff)
            this.props.vm.on('PROJECT_RUN_START', this.props.onProjectRunStart)
            this.props.vm.on('PROJECT_RUN_STOP', this.props.onProjectRunStop)
            this.props.vm.on('PROJECT_CHANGED', this.handleProjectChanged)
            this.props.vm.on('RUNTIME_STARTED', this.props.onRuntimeStarted)
            this.props.vm.on('PROJECT_START', this.props.onGreenFlag)
            this.props.vm.on(
                'PERIPHERAL_CONNECTION_LOST_ERROR',
                this.props.onShowExtensionAlert
            )
            this.props.vm.on('MIC_LISTENING', this.props.onMicListeningUpdate)
        }
        componentDidMount() {
            if (this.props.attachKeyboardEvents) {
                document.addEventListener('keydown', this.handleKeyDown)
                document.addEventListener('keyup', this.handleKeyUp)
            }
            this.props.vm.postIOData('userData', {
                username: this.props.username,
            })

            if (sessionStorage.getItem('procedure_code') !== undefined)
                sessionStorage.setItem('procedure_code', '')
        }
        componentDidUpdate(prevProps) {
            if (prevProps.username !== this.props.username) {
                this.props.vm.postIOData('userData', {
                    username: this.props.username,
                })
            }

            // Re-request a targets update when the shouldUpdateTargets state changes to true
            // i.e. when the editor transitions out of fullscreen/player only modes
            if (
                this.props.shouldUpdateTargets &&
                !prevProps.shouldUpdateTargets
            ) {
                this.props.vm.emitTargetsUpdate(
                    false /* Emit the event, but do not trigger project change */
                )
            }
        }
        componentWillUnmount() {
            this.props.vm.removeListener(
                'PERIPHERAL_CONNECTION_LOST_ERROR',
                this.props.onShowExtensionAlert
            )
            if (this.props.attachKeyboardEvents) {
                document.removeEventListener('keydown', this.handleKeyDown)
                document.removeEventListener('keyup', this.handleKeyUp)
            }
        }
        handleProjectChanged() {
            if (
                this.props.shouldUpdateProjectChanged &&
                !this.props.projectChanged
            ) {
                this.props.onProjectChanged()
            }
            this.debouncedCodeGenerator()
        }
        handleTargetsUpdate(data) {
            if (this.props.shouldUpdateTargets) {
                this.props.onTargetsUpdate(data)
            }
        }
        handleKeyDown(e) {
            // Don't capture keys intended for Blockly inputs.
            if (e.target !== document && e.target !== document.body) return

            const key = !e.key || e.key === 'Dead' ? e.keyCode : e.key
            this.props.vm.postIOData('keyboard', {
                key: key,
                isDown: true,
            })

            // Prevent space/arrow key from scrolling the page.
            if (
                e.keyCode === 32 || // 32=space
                (e.keyCode >= 37 && e.keyCode <= 40)
            ) {
                // 37, 38, 39, 40 are arrows
                e.preventDefault()
            }
        }
        handleKeyUp(e) {
            // Always capture up events,
            // even those that have switched to other targets.
            const key = !e.key || e.key === 'Dead' ? e.keyCode : e.key
            this.props.vm.postIOData('keyboard', {
                key: key,
                isDown: false,
            })

            // E.g., prevent scroll.
            if (e.target !== document && e.target !== document.body) {
                e.preventDefault()
            }
        }

        /**
         * Special Characters for MicroPython Code Generation
         * --------------------------------------------------
         * In MicroPython, indentation defines code blocks instead of curly braces `{}`.
         * To make Scratch-style block compilation easier, we insert two invisible
         * Unicode "Private Use Area" characters in intermediate code:
         *
         *   \ue000  → BLOCK START marker
         *   \ue001  → BLOCK END marker
         *
         * These characters NEVER appear in valid Python/MicroPython code.
         *
         * Why we use these markers:
         * -------------------------
         * 1. Helps detect where a block begins (e.g., loops, if-blocks)
         * 2. Helps detect where a block ends (equivalent of `}`)
         * 3. Allows building all lines first, then applying indentation logic later
         * 4. Prevents accidental clashes with normal Python syntax
         *
         * During final code generation:
         * -----------------------------
         * - We remove both markers from lines
         * - \ue000 increases indentation AFTER printing the line
         * - \ue001 decreases indentation BEFORE printing the next line
         *
         * Result:
         * -------
         * Properly indented MicroPython code without braces, fully compatible with Python's syntax.
         */
        indentCProgram = (cCode, spacesPerIndent) => {
            var lines = cCode.split('\n')
            var currentIndent = 0

            var blockStartPattern = /(\ue000\s*$)|({\s*$)/
            var blockEndPattern = /(^\s*\ue001)|(^\s*})/
            var indentedCode = ''
            for (var i = 0; i < lines.length; i++) {
                var line = lines[i].trim()
                var blockEndMatch = line.match(blockEndPattern)
                if (blockEndMatch) {
                    currentIndent -= spacesPerIndent
                }
                for (var j = 0; j < currentIndent; j++) {
                    indentedCode += ' '
                }

                // ---------------------------
                //  REMOVE SPECIAL CHARACTERS
                // ---------------------------
                let neLine = line
                    .replace(/\ue000/g, '')
                    .replace(/\ue001/g, '')
                    .trim()

                indentedCode += neLine
                indentedCode += '\n'
                var blockStartMatch = line.match(blockStartPattern)
                if (blockStartMatch) {
                    currentIndent += spacesPerIndent
                }
            }

            return indentedCode
        }
        codeGenerator = () => {
            setTimeout(() => {
                parseEachBlock(this.props.vm.editingTarget?.blocks?._blocks)
                if (sessionStorage.getItem('C_code') !== '') {
                    var codeArray = JSON.parse(sessionStorage.getItem('C_code'))
                    if (!this.props.isMicroPythonMode) {
                        codeArray.unshift('void app_main(void){')
                    }
                    if (sessionStorage.getItem('procedure_code') !== undefined)
                        if (sessionStorage.getItem('procedure_code') !== '') {
                            var procedurecodeArray = JSON.parse(
                                sessionStorage.getItem('procedure_code')
                            )
                            codeArray.unshift(procedurecodeArray.join('\n'))
                        }
                    // console.log('HHHHHHHHHHHHHHHHH', codeArray)

                    if (this.props.isMicroPythonMode) {
                        codeArray.unshift('from playcomputer import *')
                    } else {
                        codeArray.unshift('#include <playcomputer.h>')
                        codeArray.push('}')
                    }
                    const indentCcode = this.indentCProgram(
                        codeArray.join('\n'),
                        4
                    )
                    // setC_code(indentCcode)

                    // console.log(indentCcode)
                    this.props.setCodes(indentCcode)
                }
            }, 200)
        }

        debouncedCodeGenerator() {
            const WAIT = 300

            // If timer is NOT running → this is the FIRST call → run immediately
            if (!this.codeGenTimer) {
                this.codeGenerator() // immediate execution

                // Start blocking timer
                this.codeGenTimer = setTimeout(() => {
                    this.codeGenTimer = null

                    // If trailing call is pending, run it now
                    if (this.codeGenPending) {
                        this.codeGenPending = false
                        this.codeGenerator()
                    }
                }, WAIT)

                return
            }

            // If timer IS running → store that a trailing call should run
            this.codeGenPending = true
        }

        render() {
            const {
                /* eslint-disable no-unused-vars */
                attachKeyboardEvents,
                projectChanged,
                shouldUpdateTargets,
                shouldUpdateProjectChanged,
                onBlockDragUpdate,
                onGreenFlag,
                onKeyDown,
                onKeyUp,
                onMicListeningUpdate,
                onMonitorsUpdate,
                onTargetsUpdate,
                onProjectChanged,
                onProjectRunStart,
                onProjectRunStop,
                onProjectSaved,
                onRuntimeStarted,
                onTurboModeOff,
                onTurboModeOn,
                onShowExtensionAlert,
                /* eslint-enable no-unused-vars */
                ...props
            } = this.props
            return <WrappedComponent {...props} />
        }
    }
    VMListener.propTypes = {
        attachKeyboardEvents: PropTypes.bool,
        onBlockDragUpdate: PropTypes.func.isRequired,
        onGreenFlag: PropTypes.func,
        onKeyDown: PropTypes.func,
        onKeyUp: PropTypes.func,
        onMicListeningUpdate: PropTypes.func.isRequired,
        onMonitorsUpdate: PropTypes.func.isRequired,
        onProjectChanged: PropTypes.func.isRequired,
        onProjectRunStart: PropTypes.func.isRequired,
        onProjectRunStop: PropTypes.func.isRequired,
        onProjectSaved: PropTypes.func.isRequired,
        onRuntimeStarted: PropTypes.func.isRequired,
        onShowExtensionAlert: PropTypes.func.isRequired,
        onTargetsUpdate: PropTypes.func.isRequired,
        onTurboModeOff: PropTypes.func.isRequired,
        onTurboModeOn: PropTypes.func.isRequired,
        projectChanged: PropTypes.bool,
        shouldUpdateTargets: PropTypes.bool,
        shouldUpdateProjectChanged: PropTypes.bool,
        username: PropTypes.string,
        vm: PropTypes.instanceOf(VM).isRequired,
    }
    VMListener.defaultProps = {
        attachKeyboardEvents: true,
        onGreenFlag: () => ({}),
    }
    const mapStateToProps = (state) => ({
        projectChanged: state.scratchGui.projectChanged,
        // Do not emit target or project updates in fullscreen or player only mode
        // or when recording sounds (it leads to garbled recordings on low-power machines)
        shouldUpdateTargets:
            !state.scratchGui.mode.isFullScreen &&
            !state.scratchGui.mode.isPlayerOnly &&
            !state.scratchGui.modals.soundRecorder,
        // Do not update the projectChanged state in fullscreen or player only mode
        shouldUpdateProjectChanged:
            !state.scratchGui.mode.isFullScreen &&
            !state.scratchGui.mode.isPlayerOnly,
        vm: state.scratchGui.vm,
        username:
            state.session && state.session.session && state.session.session.user
                ? state.session.session.user.username
                : '',
        isMicroPythonMode:
            state.scratchGui.script.currentMode.isMicroPythonMode,
    })
    const mapDispatchToProps = (dispatch) => ({
        onTargetsUpdate: (data) => {
            dispatch(updateTargets(data.targetList, data.editingTarget))
        },
        onMonitorsUpdate: (monitorList) => {
            dispatch(updateMonitors(monitorList))
        },
        onBlockDragUpdate: (areBlocksOverGui) => {
            dispatch(updateBlockDrag(areBlocksOverGui))
        },
        onProjectRunStart: () => dispatch(setRunningState(true)),
        onProjectRunStop: () => dispatch(setRunningState(false)),
        onProjectChanged: () => dispatch(setProjectChanged()),
        onProjectSaved: () => dispatch(setProjectUnchanged()),
        onRuntimeStarted: () => dispatch(setStartedState(true)),
        onTurboModeOn: () => dispatch(setTurboState(true)),
        onTurboModeOff: () => dispatch(setTurboState(false)),
        onShowExtensionAlert: (data) => {
            dispatch(showExtensionAlert(data))
        },
        onMicListeningUpdate: (listening) => {
            dispatch(updateMicIndicator(listening))
        },
        setCodes: (codes) => {
            dispatch(setCodes(codes))
        },
    })
    return connect(mapStateToProps, mapDispatchToProps)(VMListener)
}

export default vmListenerHOC
